/*
  MASTER — Orquestación CRO (runtime).

  Tres responsabilidades, y ninguna de ellas decide si un módulo existe:

    1. PRESENTACIÓN. Cada señal declara en su propio ajuste si quiere salir
       destacada o compacta. El motor se limita a traducir esa elección a un
       atributo que el CSS entiende.
    2. AGRUPACIÓN. Las señales compactas consecutivas se meten en una fila para
       que ocupen ancho en vez de alto. Es la diferencia entre una columna de
       tarjetas y una línea de datos en un teléfono de 360.
    3. REVELADO. Aparición progresiva de secciones al entrar en viewport.

  QUÉ CAMBIÓ Y POR QUÉ
  --------------------
  Hasta v0.12.4.1 este archivo repartía protagonismo por conteo: cada zona tenía
  un cupo de señales destacadas, se ordenaban por una prioridad fija escrita
  aquí dentro y las que no cabían bajaban a compactas. Suena razonable y no lo
  es: significaba que encender una señal nueva degradaba en silencio a otra que
  el merchant no había tocado, y que la única forma de elegir cuál iba destacada
  era pelearse con una tabla de prioridades que no está en el editor. El cupo
  también dependía del modo CRO global, así que el mismo producto se presentaba
  distinto por un ajuste de otra pantalla.

  Ahora la decisión es del merchant, señal por señal, y es explícita. El motor
  coordina —agrupa, encola, desplaza— pero no suprime.
*/
/*
  COLA DE CAPAS FLOTANTES.

  Varias capacidades pueden querer la misma esquina a la vez: avisos de compra
  reciente, notificaciones, lo que venga después. La respuesta anterior del motor
  era un tope global —«como mucho N avisos por sesión»— que resolvía el choque
  borrando capacidades configuradas.

  Esta cola resuelve el mismo choque sin perder nada: quien pide turno y lo
  encuentra ocupado espera, y sale en cuanto se libera. Si aun así hay varias
  capas vivas, cada una recibe un desplazamiento propio para no solaparse.
  Presentación de una en una: sí. Descartar una capacidad: nunca.
*/
window.MasterFloatQueue = window.MasterFloatQueue || (() => {
  const waiting = [];
  const offsets = new Map();
  let active = null;
  let timer = null;

  const pump = () => {
    if (active || !waiting.length) return;
    const job = waiting.shift();
    if (!job.node.isConnected) { pump(); return; }
    active = job;
    /* Un hueco propio por capa viva, para que dos flotantes simultáneas no se
       pisen aunque compartan esquina. */
    if (!offsets.has(job.node)) offsets.set(job.node, offsets.size * 84);
    job.node.style.setProperty('--float-offset', `${offsets.get(job.node)}px`);
    try { job.run(); } catch (_) { /* un módulo roto no puede bloquear la cola */ }
    clearTimeout(timer);
    timer = setTimeout(() => { active = null; pump(); }, Math.max(1200, job.hold) + 400);
  };

  return {
    request(node, hold, run) {
      /* Una misma capa no acumula turnos: si ya espera, se queda con el último
         contenido en vez de encolar diez veces lo mismo. */
      const queued = waiting.find(j => j.node === node);
      if (queued) { queued.run = run; queued.hold = hold; return; }
      waiting.push({ node, hold, run });
      pump();
    },
    release(node) {
      const i = waiting.findIndex(j => j.node === node);
      if (i >= 0) waiting.splice(i, 1);
      offsets.delete(node);
      if (active && active.node === node) { active = null; clearTimeout(timer); pump(); }
    }
  };
})();

(() => {
  if (window.MasterCroLoaded) return;
  window.MasterCroLoaded = true;

  const P = window.MasterCRO || {};
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const reducedMotion = () =>
    P.respectReducedMotion !== false &&
    Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /*
    Un wrapper de bloque cuyo `{% if show %}` salió falso llega al DOM como div
    vacío. No hay que agruparlo con nadie: no pinta nada.
  */
  const carriesContent = node => node.children.length > 0 || node.textContent.trim() !== '';
  const onScreen = node => node.offsetParent !== null || node.getClientRects().length > 0;

  /* ---------- 1. presentación ---------- */

  const ungroup = region => {
    region.querySelectorAll(':scope > .master-cro-row').forEach(row => {
      row.replaceWith(...row.childNodes);
    });
  };

  /*
    Traducción directa de lo que pidió el merchant. No hay orden, ni conteo, ni
    comparación entre señales: lo que dice el bloque es lo que sale.
  */
  const applyEmphasis = region => {
    [...region.querySelectorAll('[data-cro-signal]')].forEach(node => {
      if (node.dataset.croZone === 'float') return;
      if (!carriesContent(node) || !onScreen(node)) {
        delete node.dataset.croTier;
        return;
      }
      if (node.dataset.croEmphasis === 'chip') node.dataset.croTier = 'quiet';
      else delete node.dataset.croTier;
    });
  };

  /* ---------- 2. agrupación ---------- */

  const group = region => {
    /*
      En el editor de temas no se mueve nada de sitio: Shopify resalta y reordena
      bloques por su posición real, y meterlos en un contenedor propio confunde
      esa interacción. El merchant sigue viendo el nivel compacto, sólo apilado.
    */
    if (designMode()) return;
    let row = null;
    [...region.children].forEach(child => {
      /*
        Un reloj no cabe en un chip ni comparte línea con uno: corta la fila y se
        queda como bloque compacto propio.
      */
      /* Un reloj no cabe en un chip ni comparte línea con uno: corta la fila y
         se queda como bloque compacto propio. */
      if (child.dataset.croTier !== 'quiet' || child.dataset.croSignal === 'offer_timer') {
        row = null;
        return;
      }
      if (!row) {
        row = document.createElement('div');
        row.className = 'master-cro-row';
        child.before(row);
      }
      row.appendChild(child);
    });
    /* Una fila con un solo chip no aporta nada: se deshace. */
    region.querySelectorAll(':scope > .master-cro-row').forEach(candidate => {
      if (candidate.children.length < 2) candidate.replaceWith(...candidate.childNodes);
    });
  };

  const orchestrate = scope => {
    const root = scope && scope.querySelectorAll ? scope : document;
    const regions = new Set();
    if (root.matches && root.matches('[data-cro-region]')) regions.add(root);
    (root.querySelectorAll('[data-cro-region]') || []).forEach(region => regions.add(region));
    if (root === document) document.querySelectorAll('[data-cro-region]').forEach(r => regions.add(r));
    regions.forEach(region => {
      ungroup(region);
      applyEmphasis(region);
      group(region);
    });
  };

  /* ---------- 3. revelado ---------- */

  /*
    Una sección sin altura propia no se puede revelar: lo único que hay dentro
    es contenido fuera de flujo. La CTA fija es exactamente ese caso, y armarla
    la dejaba en opacity:0 hasta que su sección de altura cero entrase en
    viewport — es decir, al final de la página. La CTA fija no aparecía nunca.
    Una opacidad menor que 1 en un ancestro tambien crea bloque contenedor para
    sus descendientes `fixed`, asi que el daño no era sólo de visibilidad.
  */
  const revealable = node =>
    node.classList.contains('shopify-section') &&
    node.getBoundingClientRect().height > 0 &&
    !node.querySelector('[data-sticky-cta]');

  const revealTargets = () => {
    const main = document.getElementById('MainContent');
    if (!main) return [];
    return [...main.children].filter(revealable);
  };

  /*
    La entrada es un evento de una sola vez. Al terminar la transición se retira
    el atributo para que la sección deje de tener aplicada ninguna regla de
    revelado: sin transform residual, sin transición viva y sin la capa de
    composición que ambas mantienen abierta.
  */
  const settle = node => {
    const done = () => node.removeAttribute('data-cro-reveal');
    node.addEventListener('transitionend', done, {once: true});
    setTimeout(done, 1200);
  };

  const initReveal = () => {
    if (!P.reveal || reducedMotion() || designMode()) return;
    if (!('IntersectionObserver' in window)) return;
    const targets = revealTargets();
    /*
      La primera sección es lo que el cliente ya tiene delante al cargar: si la
      escondemos para revelarla, el LCP parpadea. Entra directa.
    */
    targets.slice(1).forEach(node => {
      if (node.dataset.croReveal !== undefined) return;
      node.dataset.croReveal = '';
    });
    let observerWorks = false;
    const observer = new IntersectionObserver((entries, obs) => {
      observerWorks = true;
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        settle(entry.target);
        obs.unobserve(entry.target);
      });
    }, {rootMargin: '0px 0px -8% 0px', threshold: 0.02});
    targets.slice(1).forEach(node => observer.observe(node));

    /*
      Red de seguridad. El coste de que el observer no llegue a disparar nunca
      (un navegador raro, una extensión, un contenedor que deja de ser
      scrollable) es una sección invisible: contenido que el cliente paga por
      ver y no ve. Si a los 3 s no ha llegado ni una sola notificación —ni
      siquiera la inicial que el observer emite al empezar a observar— se
      considera roto y se revela todo sin animación.

      No se revela «todo por si acaso» pasado un tiempo: eso apagaría el efecto
      en las secciones de más abajo, que es justo lo que el merchant activó.
    */
    setTimeout(() => {
      if (observerWorks) return;
      observer.disconnect();
      targets.slice(1).forEach(node => node.removeAttribute('data-cro-reveal'));
    }, 3000);
  };

  /* ---------- arranque ---------- */

  const boot = () => {
    orchestrate(document);
    initReveal();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /*
    La agrupación depende de qué señales están realmente visibles, y eso cambia
    cuando un módulo se oculta a sí mismo tras evaluar su fuente de datos (por
    ejemplo stock real no rastreado). Un reintento diferido recoge esos casos
    sin obligar a cada módulo a avisar.
  */
  setTimeout(() => orchestrate(document), 350);

  document.addEventListener('shopify:section:load', event => orchestrate(event.target));
  document.addEventListener('shopify:block:select', () => orchestrate(document));
  document.addEventListener('master:variant-change', () => orchestrate(document));

  window.MasterCROReflow = () => orchestrate(document);
})();
